import { TextFieldProps } from '@mui/material';
import classNames from 'classnames';
import {
  FC,
  FocusEvent,
  ForwardedRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'react-phone-number-input';

import Input from '../Input/Input';
import classes from './PhoneInput.module.scss';

export interface HTMLCountrySelectElement extends HTMLSelectElement {
  changeCode?: (c: string) => void;
}

interface PhoneInputProps {
  value: string;
  onChange?: (value: string) => void;
  onFocus?: (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>
  ) => void;
  onBlur?: (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>
  ) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  endAdornment?: ReactNode;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  inputProps?: TextFieldProps;
  size?: 'small' | 'medium';
  selectRef?: ForwardedRef<HTMLCountrySelectElement>;
}

const PhoneInput: FC<PhoneInputProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  label,
  placeholder,
  disabled,
  className,
  endAdornment,
  helperText,
  error,
  required,
  inputProps,
  size,
  selectRef,
}) => {
  const countryRef = useRef<HTMLSelectElement>(null);

  const countries = useMemo(
    () =>
      getCountries()
        .map((c) => getCountryCallingCode(c))
        .filter((x, i, a) => a.indexOf(x) === i)
        .sort(),
    []
  );

  const [code, setCode] = useState('386');
  const [number, setNumber] = useState('');

  useEffect(() => {
    const num = parsePhoneNumber(value);
    if (num) {
      setCode(num.countryCallingCode);
      setNumber(num.nationalNumber);
    } else {
      setNumber(value.replace(`+${code}`, ''));
    }
    // eslint-disable-next-line
  }, [value]);

  useImperativeHandle(selectRef, () => {
    return {
      ...countryRef.current,
      changeCode: (c: string) => {
        setCode(c);
      },
    };
  });

  return (
    <Input
      {...inputProps}
      value={number}
      onChange={(e) => {
        const num = parsePhoneNumber(e.target.value);
        if (num) {
          onChange(`+${num.countryCallingCode}${num.nationalNumber}`);
          setNumber(num.nationalNumber);
          setCode(num.countryCallingCode);
        } else {
          setNumber(e.target?.value);
          onChange?.(`+${code}${e.target.value}`);
        }
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      type="text"
      label={label}
      size={size}
      placeholder={placeholder}
      disabled={disabled}
      className={classNames(className, classes.Input)}
      error={error}
      required={required}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <div className={classes.CountrySelect}>
            <select
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                onChange?.(`+${e.target.value}${number}`);
              }}
              disabled={disabled}
              ref={countryRef}
            >
              {countries.map((c, i) => (
                <option value={c} key={i}>
                  {`+${c}`}
                </option>
              ))}
            </select>
          </div>
        ),
        endAdornment,
      }}
    />
  );
};

export default PhoneInput;
